<template>
  <div class="checkout-page">
    <!-- Hero -->
    <section class="page-hero">
      <div class="container">
        <h5 class="page">Complete your order securely</h5>
      </div>
    </section>

    <section class="checkout-section">
      <div class="container">
        <div class="row g-4">

          <!-- LEFT: Form -->
          <div class="col-lg-8 text-start">

            <!-- Step 1: Contact -->
            <div class="checkout-card" id="section-contact">
              <div class="card-header-custom">
                <div class="step-badge">1</div>
                <h3>Contact Information</h3>
              </div>
              <div class="card-body-custom">
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label">Full Name <span class="required">*</span></label>
                    <input
                      type="text"
                      v-model="customerDetails.name"
                      :class="['form-control', { 'is-invalid': errors.name }]"
                      placeholder="Enter your full name"
                      @input="clearError('name')"
                    />
                    <div class="invalid-feedback" v-if="errors.name">{{ errors.name }}</div>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Email Address <span class="required">*</span></label>
                    <input
                      type="email"
                      v-model="customerDetails.email"
                      :class="['form-control', { 'is-invalid': errors.email }]"
                      placeholder="your@email.com"
                      @input="clearError('email')"
                    />
                    <div class="invalid-feedback" v-if="errors.email">{{ errors.email }}</div>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Phone Number <span class="required">*</span></label>
                    <div class="input-with-prefix">
                      <span class="input-prefix">+91</span>
                      <input
                        type="tel"
                        v-model="customerDetails.phone"
                        :class="['form-control prefix-input', { 'is-invalid': errors.phone }]"
                        placeholder="98765 43210"
                        maxlength="10"
                        @input="clearError('phone')"
                      />
                    </div>
                    <div class="invalid-feedback d-block" v-if="errors.phone">{{ errors.phone }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 2: Shipping -->
            <div class="checkout-card" id="section-shipping">
              <div class="card-header-custom">
                <div class="step-badge">2</div>
                <h3>Shipping Address</h3>
              </div>
              <div class="card-body-custom">

                <!-- Same as contact toggle -->
                <div class="same-as-contact" @click="copySameAsContact">
                  <div class="toggle-check" :class="{ active: sameAsContact }">
                    <svg v-if="sameAsContact" width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  </div>
                  <span>Same as contact information</span>
                </div>

                <div class="row g-3 mt-1">
                  <div class="col-md-6">
                    <label class="form-label">Full Name <span class="required">*</span></label>
                    <input
                      type="text"
                      v-model="shippingAddress.fullName"
                      :class="['form-control', { 'is-invalid': errors.shippingFullName }]"
                      placeholder="Recipient name"
                      @input="clearError('shippingFullName')"
                    />
                    <div class="invalid-feedback" v-if="errors.shippingFullName">{{ errors.shippingFullName }}</div>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Phone Number <span class="required">*</span></label>
                    <div class="input-with-prefix">
                      <span class="input-prefix">+91</span>
                      <input
                        type="tel"
                        v-model="shippingAddress.phone"
                        :class="['form-control prefix-input', { 'is-invalid': errors.shippingPhone }]"
                        placeholder="Delivery contact"
                        maxlength="10"
                        @input="clearError('shippingPhone')"
                      />
                    </div>
                    <div class="invalid-feedback d-block" v-if="errors.shippingPhone">{{ errors.shippingPhone }}</div>
                  </div>
                  <div class="col-12">
                    <label class="form-label">Address Line 1 <span class="required">*</span></label>
                    <input
                      type="text"
                      v-model="shippingAddress.addressLine1"
                      :class="['form-control', { 'is-invalid': errors.addressLine1 }]"
                      placeholder="House/Flat No., Building Name"
                      @input="clearError('addressLine1')"
                    />
                    <div class="invalid-feedback" v-if="errors.addressLine1">{{ errors.addressLine1 }}</div>
                  </div>
                  <div class="col-12">
                    <label class="form-label">Address Line 2</label>
                    <input type="text" v-model="shippingAddress.addressLine2" class="form-control" placeholder="Street, Area" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Landmark</label>
                    <input type="text" v-model="shippingAddress.landmark" class="form-control" placeholder="Near..." />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">City <span class="required">*</span></label>
                    <input
                      type="text"
                      v-model="shippingAddress.city"
                      :class="['form-control', { 'is-invalid': errors.city }]"
                      placeholder="City"
                      @input="clearError('city')"
                    />
                    <div class="invalid-feedback" v-if="errors.city">{{ errors.city }}</div>
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">State <span class="required">*</span></label>
                    <select
                      v-model="shippingAddress.state"
                      :class="['form-control', { 'is-invalid': errors.state }]"
                      @change="clearError('state')"
                    >
                      <option value="">Select State</option>
                      <option>Andhra Pradesh</option><option>Arunachal Pradesh</option>
                      <option>Assam</option><option>Bihar</option><option>Chhattisgarh</option>
                      <option>Goa</option><option>Gujarat</option><option>Haryana</option>
                      <option>Himachal Pradesh</option><option>Jharkhand</option>
                      <option>Karnataka</option><option>Kerala</option>
                      <option>Madhya Pradesh</option><option>Maharashtra</option>
                      <option>Manipur</option><option>Meghalaya</option><option>Mizoram</option>
                      <option>Nagaland</option><option>Odisha</option><option>Punjab</option>
                      <option>Rajasthan</option><option>Sikkim</option><option>Tamil Nadu</option>
                      <option>Telangana</option><option>Tripura</option>
                      <option>Uttar Pradesh</option><option>Uttarakhand</option>
                      <option>West Bengal</option><option>Delhi</option>
                    </select>
                    <div class="invalid-feedback" v-if="errors.state">{{ errors.state }}</div>
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Pincode <span class="required">*</span></label>
                    <input
                      type="text"
                      v-model="shippingAddress.pincode"
                      :class="['form-control', { 'is-invalid': errors.pincode }]"
                      placeholder="380001"
                      maxlength="6"
                      @input="clearError('pincode')"
                    />
                    <div class="invalid-feedback" v-if="errors.pincode">{{ errors.pincode }}</div>
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Country</label>
                    <input type="text" v-model="shippingAddress.country" class="form-control" readonly />
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 3: Notes -->
            <div class="checkout-card">
              <div class="card-header-custom">
                <div class="step-badge">3</div>
                <h3>Order Notes <span class="optional-tag">Optional</span></h3>
              </div>
              <div class="card-body-custom">
                <textarea
                  v-model="customerNotes"
                  class="form-control"
                  rows="3"
                  placeholder="Any special instructions, personalisation requests, or gift messages..."
                ></textarea>
              </div>
            </div>

          </div>

          <!-- RIGHT: Order Summary -->
          <div class="col-lg-4">
            <div class="order-summary-sticky">
              <div class="checkout-card order-summary">
                <div class="summary-header">
                  <h3>Order Summary</h3>
                  <span class="item-count">{{ cartItems.length }} item{{ cartItems.length !== 1 ? 's' : '' }}</span>
                </div>
                <div class="card-body-custom">

                  <div class="summary-items">
                    <div v-for="item in cartItems" :key="item.productId" class="summary-item">
                      <div class="item-img-wrap">
                        <img :src="item.mainImage" :alt="item.productDetail.name" class="item-image" />
                        <span class="qty-bubble">{{ item.qty }}</span>
                      </div>
                      <div class="item-details">
                        <h6>{{ item.productDetail.name }}</h6>
                      </div>
                      <div class="item-price">
                        ₹{{ (item.productDetail.offerPrice || item.productDetail.price) * item.qty }}
                      </div>
                    </div>
                  </div>

                  <div class="price-breakdown">
                    <div class="price-row">
                      <span>Subtotal</span>
                      <span>₹{{ pricing.itemsTotal }}</span>
                    </div>
                    <div class="price-row">
                      <span>Shipping</span>
                      <span :class="pricing.shippingCharges === 0 ? 'free-badge' : ''">
                        {{ pricing.shippingCharges === 0 ? 'FREE' : '₹' + pricing.shippingCharges }}
                      </span>
                    </div>
                    <div v-if="pricing.shippingCharges > 0" class="free-shipping-hint">
                      Add ₹{{ 999 - pricing.itemsTotal }} more for free shipping
                    </div>
                    <div v-if="pricing.discount > 0" class="price-row discount-row">
                      <span>Discount</span>
                      <span>-₹{{ pricing.discount }}</span>
                    </div>
                    <div class="price-row total-row">
                      <span>Total</span>
                      <span>₹{{ pricing.totalAmount }}</span>
                    </div>
                  </div>

                  <!-- Desktop place order -->
                  <button @click="initiateCheckout" class="btn-place-order d-none d-lg-flex" :disabled="isProcessing">
                    <span v-if="!isProcessing">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-2px;margin-right:6px;"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                      Place Order · ₹{{ pricing.totalAmount }}
                    </span>
                    <span v-else>
                      <i class="fas fa-spinner fa-spin" style="margin-right:6px;" />
                      Processing...
                    </span>
                  </button>

                  <p class="secure-note">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-1px;margin-right:4px;"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                    Secure payment via Razorpay
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>

    <!-- Mobile sticky place order bar -->
    <div class="mobile-order-bar d-flex d-lg-none">
      <div class="mobile-total">
        <span class="mobile-total-label">Total</span>
        <span class="mobile-total-amount">₹{{ pricing.totalAmount }}</span>
      </div>
      <button class="btn-place-order-mobile" @click="initiateCheckout" :disabled="isProcessing">
        <span v-if="!isProcessing">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-2px;margin-right:5px;"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          Place Order
        </span>
        <span v-else>
          <i class="fas fa-spinner fa-spin" />
          Processing...
        </span>
      </button>
    </div>

  </div>
</template>

<script>
import { useToast } from 'vue-toast-notification';
import OrderService from '../services/OrderService';
import ProductService from '../services/ProductService';

export default {
  name: 'CheckoutPage',
  data() {
    return {
      customerDetails: { name: '', email: '', phone: '' },
      shippingAddress: {
        fullName: '', phone: '', addressLine1: '', addressLine2: '',
        landmark: '', city: '', state: '', pincode: '', country: 'India'
      },
      customerNotes: '',
      cartItems: [],
      pricing: {
        itemsTotal: 0, shippingCharges: 0, tax: 0,
        discount: 0, couponCode: null, couponDiscount: 0, totalAmount: 0
      },
      errors: {},
      isProcessing: false,
      sameAsContact: true,
    };
  },
  methods: {
    clearError(field) {
      if (this.errors[field]) {
        const e = { ...this.errors };
        delete e[field];
        this.errors = e;
      }
    },

    // "Same as contact" toggle
    copySameAsContact() {
      this.sameAsContact = !this.sameAsContact;
      if (this.sameAsContact) {
        this.shippingAddress.fullName = this.customerDetails.name;
        this.shippingAddress.phone    = this.customerDetails.phone;
      }
    },

    validateForm() {
      const e = {};
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const phoneRegex = /^[0-9]{10}$/;

      if (!this.customerDetails.name.trim())
        e.name = 'Full name is required';
      if (!emailRegex.test(this.customerDetails.email))
        e.email = 'Enter a valid email address';
      if (!phoneRegex.test(this.customerDetails.phone))
        e.phone = 'Enter a valid 10-digit phone number';
      if (!this.shippingAddress.fullName.trim())
        e.shippingFullName = 'Recipient name is required';
      if (!phoneRegex.test(this.shippingAddress.phone))
        e.shippingPhone = 'Enter a valid 10-digit phone number';
      if (!this.shippingAddress.addressLine1.trim())
        e.addressLine1 = 'Address is required';
      if (!this.shippingAddress.city.trim())
        e.city = 'City is required';
      if (!this.shippingAddress.state)
        e.state = 'Please select a state';
      if (!/^[0-9]{6}$/.test(this.shippingAddress.pincode))
        e.pincode = 'Enter a valid 6-digit pincode';

      this.errors = e;
      return Object.keys(e).length === 0;
    },

    scrollToFirstError() {
      // Map error keys to field IDs or section IDs
      const fieldMap = {
        name: 'section-contact',
        email: 'section-contact',
        phone: 'section-contact',
        shippingFullName: 'section-shipping',
        shippingPhone: 'section-shipping',
        addressLine1: 'section-shipping',
        city: 'section-shipping',
        state: 'section-shipping',
        pincode: 'section-shipping',
      };
      const firstKey = Object.keys(this.errors)[0];
      const sectionId = fieldMap[firstKey];
      if (sectionId) {
        const el = document.getElementById(sectionId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    },

    async loadCart() {
      const $toast = useToast();
      try {
        const response = await ProductService.getCartItems();
        this.cartItems = response.data.data;
        this.calculatePricing();
      } catch (error) {
        $toast.error('Failed to load cart');
        this.$router.push('/cart');
      }
    },

    calculatePricing() {
      this.pricing.itemsTotal = this.cartItems.reduce((total, item) => {
        const price = item.productDetail.offerPrice || item.productDetail.price;
        return total + (price * item.qty);
      }, 0);
      this.pricing.shippingCharges = this.pricing.itemsTotal >= 999 ? 0 : 99;
      this.pricing.totalAmount =
        this.pricing.itemsTotal + this.pricing.shippingCharges
        - this.pricing.discount - this.pricing.couponDiscount;
    },

    async initiateCheckout() {
      const $toast = useToast();

      if (!this.validateForm()) {
        this.$nextTick(() => this.scrollToFirstError());
        $toast.error('Please fix the highlighted fields');
        return;
      }

      this.isProcessing = true;
      try {
        const orderResponse = await OrderService.createOrder({
          customerDetails: this.customerDetails,
          shippingAddress: this.shippingAddress,
          cartItems: this.cartItems,
          pricing: this.pricing,
          customerNotes: this.customerNotes
        });

        const { order, razorpayOrder, customer } = orderResponse.data.data;

        const options = {
          key: process.env.VUE_APP_RAZORPAY_KEY,
          amount: razorpayOrder.amount,
          currency: razorpayOrder.currency,
          name: "Bee's Atelier",
          description: `Order #${order.orderNumber}`,
          order_id: razorpayOrder.id,
          prefill: { name: customer.name, email: customer.email, contact: customer.phone },
          theme: { color: "#76ACAF" },
          handler: async (response) => { await this.verifyPayment(response, order._id); },
          modal: { ondismiss: () => { this.isProcessing = false; $toast.info('Payment cancelled'); } }
        };

        const rzp = new window.Razorpay(options);
        rzp.on("payment.failed", (response) => {
          $toast.error(response.error.description || 'Payment failed');
        });
        rzp.open();

      } catch (error) {
        $toast.error(error.response?.data?.response_msg || 'Checkout failed');
        this.isProcessing = false;
      }
    },

    async verifyPayment(razorpayResponse, orderId) {
      const $toast = useToast();
      try {
        await OrderService.verifyPayment({
          razorpay_order_id: razorpayResponse.razorpay_order_id,
          razorpay_payment_id: razorpayResponse.razorpay_payment_id,
          razorpay_signature: razorpayResponse.razorpay_signature,
          orderId
        });
        $toast.success('Payment successful! Your order has been placed.');
        this.$router.push({ name: 'Order Success', query: { orderId } });
      } catch (error) {
        $toast.error('Payment verification failed. Please contact support.');
        this.isProcessing = false;
      }
    },
  },
  watch: {
    'customerDetails.name'(val) {
      if (this.sameAsContact) this.shippingAddress.fullName = val;
    },
    'customerDetails.phone'(val) {
      if (this.sameAsContact) this.shippingAddress.phone = val;
    },
  },
  mounted() {
    this.loadCart();
    if (typeof AOS !== 'undefined') AOS.init({ duration: 600, once: true });
    if (!document.getElementById('razorpay-script')) {
      const script = document.createElement('script');
      script.id = 'razorpay-script';
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      document.body.appendChild(script);
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Playfair+Display:wght@600;700&display=swap');

.checkout-page {
  font-family: 'Inter', sans-serif;
  background: #f8fafb;
  min-height: 100vh;
}

/* Hero */
.page-hero {
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
  padding: 60px 0 40px;
  margin-top: 80px;
  color: white;
  text-align: center;
}
.page-title {
  font-family: 'Playfair Display', serif;
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 10px;
}
.page-subtitle { font-size: 1.1rem; opacity: 0.9; }

/* Section */
.checkout-section {
  padding: 40px 0 120px;
}

/* Card */
.checkout-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
  margin-bottom: 20px;
  overflow: hidden;
}

.card-header-custom {
  padding: 20px 24px;
  border-bottom: 1px solid #f0f4f8;
  display: flex;
  align-items: center;
  gap: 14px;
}

.step-badge {
  width: 34px;
  height: 34px;
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
  flex-shrink: 0;
}

.card-header-custom h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1a202c;
  display: flex;
  align-items: center;
  gap: 8px;
}

.optional-tag {
  font-size: 0.75rem;
  font-weight: 400;
  color: #a0aec0;
  background: #f7fafc;
  padding: 2px 8px;
  border-radius: 20px;
  border: 1px solid #e2e8f0;
}

.card-body-custom { padding: 24px; }

/* Same as contact toggle */
.same-as-contact {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 0.9rem;
  color: #4a5568;
  font-weight: 500;
  user-select: none;
  margin-bottom: 4px;
}
.toggle-check {
  width: 20px;
  height: 20px;
  border-radius: 5px;
  border: 2px solid #cbd5e0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;
}
.toggle-check.active {
  background: rgb(118, 172, 175);
  border-color: rgb(118, 172, 175);
}

/* Form */
.form-label {
  font-weight: 500;
  color: #2d3748;
  margin-bottom: 6px;
  font-size: 0.9rem;
}
.required { color: #e53e3e; margin-left: 2px; }

.form-control {
  padding: 11px 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 0.95rem;
  transition: border-color 0.2s, box-shadow 0.2s;
  background: #fafbfc;
  color: #1a202c;
}
.form-control:focus {
  outline: none;
  border-color: rgb(118, 172, 175);
  box-shadow: 0 0 0 3px rgba(118, 172, 175, 0.15);
  background: white;
}
.form-control.is-invalid {
  border-color: #e53e3e;
  background: #fff5f5;
}
.form-control.is-invalid:focus {
  box-shadow: 0 0 0 3px rgba(229, 62, 62, 0.12);
}
.invalid-feedback {
  font-size: 0.8rem;
  color: #e53e3e;
  margin-top: 5px;
  display: flex;
  align-items: center;
  gap: 4px;
}
.invalid-feedback::before {
  content: '!';
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  background: #e53e3e;
  color: white;
  border-radius: 50%;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
}

/* Phone prefix */
.input-with-prefix {
  display: flex;
  align-items: stretch;
}
.input-prefix {
  padding: 11px 12px;
  background: #f0f4f8;
  border: 1.5px solid #e2e8f0;
  border-right: none;
  border-radius: 10px 0 0 10px;
  font-size: 0.9rem;
  color: #4a5568;
  font-weight: 500;
  white-space: nowrap;
}
.prefix-input {
  border-radius: 0 10px 10px 0 !important;
}

/* Order Summary */
.order-summary-sticky {
  position: sticky;
  top: 100px;
}
.summary-header {
  padding: 20px 24px;
  border-bottom: 1px solid #f0f4f8;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.summary-header h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #1a202c;
}
.item-count {
  font-size: 0.8rem;
  background: #edf2f7;
  color: #4a5568;
  padding: 3px 10px;
  border-radius: 20px;
  font-weight: 500;
}

.summary-items {
  max-height: 280px;
  overflow-y: auto;
  margin-bottom: 16px;
}
.summary-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f0f4f8;
}
.summary-item:last-child { border-bottom: none; }

.item-img-wrap {
  position: relative;
  flex-shrink: 0;
}
.item-image {
  width: 52px;
  height: 52px;
  object-fit: cover;
  border-radius: 8px;
}
.qty-bubble {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  background: rgb(118, 172, 175);
  color: white;
  font-size: 11px;
  font-weight: 700;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
}

.item-details { flex: 1; }
.item-details h6 {
  font-size: 0.88rem;
  font-weight: 500;
  color: #2d3748;
  margin: 0;
  line-height: 1.3;
}
.item-price {
  font-weight: 600;
  color: #1a202c;
  font-size: 0.95rem;
  white-space: nowrap;
}

/* Pricing */
.price-breakdown {
  border-top: 1px solid #f0f4f8;
  padding-top: 14px;
  margin-bottom: 8px;
}
.price-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 0.95rem;
  color: #4a5568;
}
.free-badge {
  color: #38a169;
  font-weight: 600;
}
.free-shipping-hint {
  font-size: 0.78rem;
  color: #e67e22;
  background: #fef9f0;
  border: 1px solid #fce4b0;
  border-radius: 6px;
  padding: 5px 10px;
  margin: 4px 0 8px;
  text-align: center;
}
.discount-row { color: #38a169; }
.total-row {
  display: flex;
  justify-content: space-between;
  padding: 12px 0 4px;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1a202c;
  border-top: 1px solid #e2e8f0;
  margin-top: 4px;
}

/* Place Order — desktop */
.btn-place-order {
  width: 100%;
  padding: 15px;
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.2s;
  margin-top: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.btn-place-order:hover:not(:disabled) {
  opacity: 0.92;
  transform: translateY(-1px);
}
.btn-place-order:disabled { opacity: 0.65; cursor: not-allowed; }

.secure-note {
  text-align: center;
  font-size: 0.8rem;
  color: #a0aec0;
  margin-top: 10px;
  margin-bottom: 0;
}

/* Mobile sticky bar */
.mobile-order-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: white;
  border-top: 1px solid #e2e8f0;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  box-shadow: 0 -4px 16px rgba(0,0,0,0.08);
}
.mobile-total {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}
.mobile-total-label {
  font-size: 11px;
  color: #a0aec0;
}
.mobile-total-amount {
  font-size: 18px;
  font-weight: 700;
  color: #1a202c;
}
.btn-place-order-mobile {
  flex: 1;
  padding: 13px 20px;
  background: linear-gradient(135deg, rgb(118, 172, 175), rgb(98, 152, 155));
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;
}
.btn-place-order-mobile:disabled { opacity: 0.65; cursor: not-allowed; }

/* Mobile spacing for sticky bar */
@media (max-width: 991px) {
  .checkout-section { padding-bottom: calc(90px + env(safe-area-inset-bottom)); }
  .page-hero { margin-top: 70px; padding: 40px 0 30px; }
  .order-summary-sticky { position: static; }
  .card-body-custom { padding: 16px; }
  .card-header-custom { padding: 16px; }
}
</style>